class HeroCarousel extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;

    this.track = this.querySelector('[data-carousel-track]');
    this.slides = [...this.querySelectorAll('[data-carousel-slide]')];
    this.root = this.closest('[data-hero-banner]');
    this.dots = this.root ? [...this.root.querySelectorAll('[data-carousel-dot]')] : [];
    this.count = this.slides.length;
    this.reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.desktopQuery = window.matchMedia('(min-width: 1024px)');
    this.controller = new AbortController();
    const { signal } = this.controller;

    this.desktopQuery.addEventListener('change', () => this.syncVideos(), { signal });

    // Safari (Low Power Mode, "Never Auto-Play") refuses play() until the first user gesture, so retry then.
    for (const type of ['pointerdown', 'touchend', 'keydown']) {
      document.addEventListener(type, () => this.syncVideos(), { signal, once: true });
    }
    // Back/forward cache restores and tab switches leave the active slide's video paused.
    window.addEventListener('pageshow', () => this.syncVideos(), { signal });
    document.addEventListener('visibilitychange', () => !document.hidden && this.syncVideos(), { signal });

    // Non-first slides ship their media hidden so it never competes with the LCP image; show it after load,
    // or at once when the visitor reaches for the carousel.
    if (document.readyState === 'complete') {
      this.revealDeferred();
    } else {
      window.addEventListener('load', () => this.revealDeferred(), { signal, once: true });
      this.addEventListener('pointerdown', () => this.revealDeferred(), { signal, once: true });
    }

    if (!this.track || this.count <= 1 || typeof EmblaCarousel === 'undefined') {
      this.syncVideos(0);
      return;
    }

    this.autoplay = this.dataset.autoplay === 'true';
    this.speed = (Number.parseInt(this.dataset.autoplaySpeed, 10) || 5) * 1000;
    this.focusWithin = false;
    this.editorPaused = false;

    this.embla = EmblaCarousel(this, {
      loop: true,
      align: 'start',
      duration: this.reduced ? 1 : 20,
    });

    this.embla.on('select', () => this.updateState());
    this.updateState();

    for (const [index, dot] of this.dots.entries()) {
      dot.addEventListener('click', () => this.select(index), { signal });
    }

    this.root?.addEventListener(
      'focusin',
      () => {
        this.focusWithin = true;
        this.stop();
      },
      { signal },
    );
    this.root?.addEventListener(
      'focusout',
      (event) => {
        this.focusWithin = this.root?.contains(event.relatedTarget) ?? false;
        this.start();
      },
      { signal },
    );
    document.addEventListener('visibilitychange', () => (document.hidden ? this.stop() : this.start()), { signal });
    this.start();

    document.addEventListener('shopify:block:select', (event) => this.onBlockSelect(event), { signal });
    document.addEventListener('shopify:block:deselect', () => this.onBlockDeselect(), { signal });
  }

  disconnectedCallback() {
    this.stop();
    this.syncVideos(-1);
    this.controller?.abort();
    this.embla?.destroy();
    this.embla = undefined;
    this.initialized = false;
  }

  revealDeferred(scope = this) {
    for (const media of scope.querySelectorAll('[data-hero-deferred][hidden]')) media.hidden = false;
  }

  updateState() {
    const selected = this.embla.selectedScrollSnap();
    this.revealDeferred(this.slides[selected]);

    for (const [index, dot] of this.dots.entries()) {
      dot.setAttribute('aria-current', index === selected ? 'true' : 'false');
    }

    // inert, not aria-hidden: it also takes the off-screen slides' CTA links out of the tab order.
    for (const [index, slide] of this.slides.entries()) {
      slide.inert = index !== selected;
    }

    this.syncVideos(selected);
  }

  syncVideos(selected = this.embla?.selectedScrollSnap() ?? 0) {
    const viewport = this.desktopQuery.matches ? 'desktop' : 'mobile';

    for (const [index, slide] of this.slides.entries()) {
      for (const wrapper of slide.querySelectorAll('[data-hero-video]')) {
        const video = wrapper.querySelector('video');
        if (!video) continue;

        if (index === selected && wrapper.dataset.heroVideo === viewport && !this.reduced) {
          video.muted = true;
          video.play().catch(() => {});
        } else {
          video.pause();
        }
      }
    }
  }

  select(index) {
    this.embla.scrollTo(index);
    this.start();
  }

  start() {
    this.stop();
    if (!this.autoplay || this.reduced || this.focusWithin || this.editorPaused || document.hidden) return;
    this.timer = setInterval(() => this.embla.scrollNext(), this.speed);
  }

  stop() {
    clearInterval(this.timer);
    this.timer = undefined;
  }

  onBlockSelect(event) {
    const slide = event.target?.closest?.('[data-carousel-slide]');
    if (!slide || !this.slides.includes(slide)) return;
    this.editorPaused = true;
    this.stop();
    this.embla.scrollTo(this.slides.indexOf(slide), true);
  }

  onBlockDeselect() {
    this.editorPaused = false;
    this.start();
  }
}

if (!customElements.get('hero-carousel')) {
  customElements.define('hero-carousel', HeroCarousel);
}
